import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { type ShiftEntry } from "@/mocks/admin/crew";
import { useShifts } from "@/hooks/admin/useAdminData";

// helper: compute live hours since clockIn (assumes today)
function hoursSince(clockIn: string): string {
  const [h, m] = clockIn.split(":").map(Number);
  const now = new Date();
  const start = new Date();
  start.setHours(h, m, 0, 0);
  const diffMs = now.getTime() - start.getTime();
  if (diffMs <= 0) return "0:00";
  const totalMin = Math.floor(diffMs / 60000);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return `${hh}:${String(mm).padStart(2, "0")}`;
}

export default function ActiveShifts() {
  const { active: liveShifts, loading, refetch } = useShifts();
  const [shifts, setShifts] = useState<ShiftEntry[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setShifts(liveShifts);
  }, [liveShifts]);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const clockOut = async (id: string) => {
    const shift = shifts.find((s) => s.id === id);
    if (!shift) return;
    const newClockOut = new Date().toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const hoursFloat = parseFloat(hoursSince(shift.clockIn).split(":")[0]) + 0.25;

    // optimistic UI update
    setShifts((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, status: "pending-approval", clockOut: newClockOut, totalHours: hoursFloat }
          : s,
      ),
    );

    await supabase
      .from("shifts")
      .update({
        status: "pending-approval",
        clock_out: newClockOut,
        total_hours: hoursFloat,
      })
      .eq("id", id);
    refetch();
  };

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-4 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">On The Clock Right Now</h3>
          <p className="text-[11px] text-ink-500 mt-0.5">
            {shifts.filter((s) => s.status === "active" || s.status === "on-break").length} active
            shifts · auto-refreshing
          </p>
        </div>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>
          Manual Punch
        </button>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        {loading && shifts.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-500">
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            Loading active shifts…
          </div>
        ) : (
        <table className="w-full text-sm" key={tick}>
          <thead>
            <tr className="bg-cream-50 border-b border-ink-100 text-ink-500">
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Crew
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Project
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Clocked In
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Elapsed
              </th>
              <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Status
              </th>
              <th className="text-right font-semibold uppercase text-[10px] tracking-wider px-5 py-3">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((s) => {
              const elapsed = s.totalHours
                ? `${s.totalHours.toFixed(2)}h`
                : hoursSince(s.clockIn);
              const isLive = s.status === "active" || s.status === "on-break";
              return (
                <tr
                  key={s.id}
                  className="border-b border-ink-100 last:border-b-0 hover:bg-cream-50 transition-colors"
                >
                  <td className="px-5 py-3">
                    <Link
                      to={`/crew/${s.crewId}`}
                      className="flex items-center gap-3 group cursor-pointer"
                    >
                      <div
                        className={`w-9 h-9 rounded-full ${s.color} text-white text-xs font-bold flex items-center justify-center shrink-0`}
                      >
                        {s.initials}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-ink-900 group-hover:text-brand-700 truncate">
                          {s.crewName}
                        </div>
                        <div className="text-[11px] text-ink-500">{s.role}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-5 py-3">
                    <Link
                      to={`/projects/${s.projectId}`}
                      className="font-mono text-[11px] font-bold text-brand-700 hover:underline cursor-pointer"
                    >
                      {s.projectId}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-sm font-semibold text-ink-900 tabular-nums">
                    {s.clockIn}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`font-display text-base font-bold tabular-nums ${
                        isLive ? "text-brand-700" : "text-ink-700"
                      }`}
                    >
                      {elapsed}
                    </span>
                    {s.break > 0 && (
                      <div className="text-[10px] text-ink-400">{s.break}m break</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {s.status === "active" && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        Active
                      </span>
                    )}
                    {s.status === "on-break" && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                        On Break
                      </span>
                    )}
                    {s.status === "completed" && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-ink-100 text-ink-700">
                        Completed
                      </span>
                    )}
                    {s.status === "pending-approval" && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700">
                        Pending Approval
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {isLive ? (
                      <button
                        onClick={() => clockOut(s.id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-logout-box-r-line"></i>
                        Clock Out
                      </button>
                    ) : (
                      <span className="text-[11px] text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}